import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Input, Modal} from 'antd'
import {MIN_CHANGE_REASON} from '../../constants'
import {formatDecimal, formatExactRate} from '../../helpers/format'

const {TextArea} = Input

/**
 * Hộp thoại M2 — nhập lý do khi chỉ số lệch quá ±30% (§6 · VAL-05).
 *
 * ⚠️ Sau khi bỏ bước duyệt và bỏ trường nguồn số liệu, **đây là lớp kiểm soát duy nhất còn lại**.
 *    Vì vậy nút xác nhận bị khoá tới khi lý do đủ `MIN_CHANGE_REASON` ký tự, và "Quay lại" hủy TOÀN BỘ thao tác lưu
 *    chứ không phải chỉ đóng hộp thoại.
 *
 * Nhiều nhóm cùng lệch quá 30% thì liệt kê tất cả nhưng dùng **một ô lý do chung**.
 */
function ChangeReasonModal({visible, deltas, onCancel, onConfirm, loading}) {
    const [reason, setReason] = useState('')

    /*
     * Dọn ô lý do mỗi lần MỞ, không phải mỗi lần đóng.
     *
     * Component nằm thường trực trong cây render của StageConfigPage và chỉ bật/tắt bằng `visible`,
     * nên không có gì tự dọn state. Thiếu effect này thì lần lưu sau mở ra đã sẵn lý do của lần
     * trước và nút "Xác nhận lưu" ĐÃ BẬT (vì đủ `MIN_CHANGE_REASON` ký tự) — người dùng bấm qua là ghi vào nhật ký
     * một lý do không liên quan, đúng thứ mà lớp kiểm soát này sinh ra để chặn.
     *
     * Dọn lúc mở chứ không lúc đóng để chữ không biến mất giữa animation đóng của antd.
     * (`destroyOnClose` không dùng được: state nằm ở component NÀY, không nằm trong children
     * của <Modal> nên antd không huỷ giúp.)
     */
    useEffect(() => {
        if (visible) setReason('')
    }, [visible])

    return (
        <Modal
            title="Giá trị lệch quá 30% so với version trước"
            visible={visible}
            onCancel={onCancel}
            onOk={() => onConfirm(reason)}
            confirmLoading={loading}
            okText="Xác nhận lưu"
            cancelText="Quay lại"
            okButtonProps={{disabled: reason.trim().length < MIN_CHANGE_REASON}}
        >
            <div className="VcWarnBox">
                {(deltas || []).map((item) => (
                    <div key={item.index}>
                        Nhóm {item.index + 1}: chỉ số cũ {formatExactRate(item.oldRate)} → mới{' '}
                        {formatExactRate(item.newRate)}
                        {/*
                          * `deltaPercent === null` = chỉ số cũ là 0, phần trăm lệch vô định. In
                          * "lệch null%" hay "lệch Infinity%" đều vô nghĩa, mà đây lại là ca ĐÁNG
                          * giải thích nhất: nhóm chuyển từ "không đi qua công đoạn" sang có định
                          * mức thật.
                          */}
                        {item.deltaPercent === null
                            ? ' (từ 0 — không qua công đoạn — thành có định mức).'
                            : ` (lệch ${formatDecimal(item.deltaPercent, 1)}%).`}
                    </div>
                ))}
                <div className="mt-2">
                    Sau khi bỏ bước duyệt và bỏ trường nguồn số liệu, đây là lớp kiểm soát duy nhất
                    còn lại.
                </div>
            </div>

            <div className="VcField mt-3">
                <label>Lý do thay đổi <span className="VcDanger">*</span></label>
                <TextArea
                    rows={3}
                    value={reason}
                    placeholder={`Tối thiểu ${MIN_CHANGE_REASON} ký tự`}
                    onChange={(event) => setReason(event.target.value)}
                />
            </div>
        </Modal>
    )
}

ChangeReasonModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    deltas: PropTypes.array,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    loading: PropTypes.bool,
}

export default ChangeReasonModal
